import { useState } from "react"
import {
  Card,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Pagination,
  Typography,
  Skeleton,
} from "@mui/material"
import DownloadIcon from "@mui/icons-material/Download"
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline"
import { useQuery } from "@tanstack/react-query"
import { toast } from "sonner"
import dayjs from "dayjs"
import { apiFetch, defaultHeaders } from "@/hooks/apiFetch"
import StatusBadge from "@/components/ui/badge"

const ACTIVE_STATUSES = ["pending", "processing"]

export default function ExportList() {
  const [page, setPage] = useState(1)

  const { data: apiResponse, isLoading } = useQuery({
    queryKey: ["exports", page],
    queryFn: ({ signal }) => apiFetch(`exports?page=${page}`, { signal }),
    refetchInterval: (query) => {
      const rows = query.state.data?.data?.data ?? []
      return rows.some((row) => ACTIVE_STATUSES.includes(row.status))
        ? 3000
        : false
    },
  })

  const rows = apiResponse?.data?.data ?? []
  const meta = apiResponse?.data?.meta

  const downloadExport = async (row) => {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_BASE_URL}/${row.download_path}`,
        { headers: defaultHeaders() }
      )

      if (!response.ok) {
        const err = await response.json().catch(() => ({}))
        toast.error(err.message || "Failed to download file")
        return
      }

      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = url
      link.download = row.file_name || "export.xlsx"
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error("Error downloading export:", error)
      toast.error("Failed to download file")
    }
  }

  const formatDate = (value, withTime = false) =>
    value ? dayjs(value).format(withTime ? "YYYY-MM-DD HH:mm" : "YYYY-MM-DD") : "—"

  return (
    <Card sx={{ p: 2 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <Typography variant="h6">Exports</Typography>
      </Box>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>From</TableCell>
              <TableCell>To</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Requested At</TableCell>
              <TableCell>Completed At</TableCell>
              <TableCell align="center">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading &&
              [...Array(5)].map((_, i) => (
                <TableRow key={i}>
                  {[...Array(7)].map((_, j) => (
                    <TableCell key={j}>
                      <Skeleton />
                    </TableCell>
                  ))}
                </TableRow>
              ))}

            {!isLoading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  No exports yet. Use the Export button on any list page to
                  create one.
                </TableCell>
              </TableRow>
            )}

            {rows.map((row) => (
              <TableRow key={row.id}>
                <TableCell>{row.name || row.model}</TableCell>
                <TableCell>{formatDate(row.from_date)}</TableCell>
                <TableCell>{formatDate(row.to_date)}</TableCell>
                <TableCell>
                  <StatusBadge name={row.status} />
                </TableCell>
                <TableCell>{formatDate(row.created_at, true)}</TableCell>
                <TableCell>{formatDate(row.completed_at, true)}</TableCell>
                <TableCell align="center">
                  {row.status === "completed" && row.download_path && (
                    <Tooltip title="Download">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => downloadExport(row)}
                      >
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {row.status === "failed" && (
                    <Tooltip title={row.error_message || "Export failed"}>
                      <ErrorOutlineIcon color="error" fontSize="small" />
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {meta && meta.last_page > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
          <Pagination
            count={meta.last_page}
            page={meta.current_page}
            onChange={(_, value) => setPage(value)}
          />
        </Box>
      )}
    </Card>
  )
}
